"use client";

import HcpAccordion from '@/components-hcp/accordion';
import CustomImage from '@/components/image';
import styles from './_androgen-related.module.scss'; //switched to tailwind util classes

const footnotesData1 = [
  [
    {
      text: <>Changes in hirsutism from baseline to week 28 were measured using a visual analog scale, where 0 indicates no symptoms and 100 indicates very severe symptoms.<sup>10,11</sup></>,
    },
  ],
];

const footnotesData2 = [
  [
    {
      text: <>Percentages are number of patients who experienced a reduction in androstenedione-to-testosterone ratio from &ge;0.5 at baseline to &lt;0.5 at week 28.<sup>10</sup></>,
    },
  ],
];

export default function HcpClinicalInformationAndrogenRelated() {
  const GraphSection = ({ subheading, imageSrc, imageAlt = '', footnotes, imageWidth, imageHeight }, i) => (
    <div className={styles.graphSection}>
      <div className="">
        <p className={styles.graphText}>
          {subheading}
        </p>
        <div className="mb-4">
          <CustomImage
            src={imageSrc}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
          />
        </div>
      </div>
      {footnotes && <Footnotes footnotes={footnotes} />}
  
    </div>
  );
  
  const Footnotes = ({ footnotes }) => (
    <div className={styles.footnotes}>
      {footnotes.map((column, index) => (
        <div key={index} className="w-100">
          <ul className="footnotes ">
            {column.map(({supHang, text}, idx) => (
              <li key={idx} className="mb-1">
                {supHang && <sup className="hang">{supHang}</sup>}
                <span>
                  {text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <section className={styles.container}>
      <HcpAccordion label="View more androgen-related endpoints" anchorId="androgen-releted">

        <div className="container">
          <h2 className='page-heading h1 mobile:h2'>
            Changes in symptoms of <br className="hidden tablet:block"/>androgen excess at week 28
          </h2>

          <div className={styles.graphsWrapper}>
            <GraphSection
              subheading={<>Hirsutism scores (female patients)<sup>10,11</sup><span className="super-asterisc">*</span></>}
              imageSrc="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Hirsutism-Scores.svg"
              imageWidth="570"
              imageHeight="594"
              imageAlt="Patients taking CRENESSITY experienced a mean reduction of 7.0 in hirsutism scores vs an increase of 2.3 with placebo."
              footnotes={footnotesData1}
            />
            <GraphSection
              subheading={<>Achieved reduction in androstenedione-to-testosterone ratio (male patients)<sup>10</sup></>}
              imageSrc="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Reduction-in-Androstenedione.svg"
              imageWidth="594"
              imageHeight="570"
              imageAlt="38% of patients taking CRENESSITY experienced a reduction in androstenedione-to-testosterone ratio vs 0% with placebo."
              footnotes={footnotesData2}
            />
          </div>
          <div className={styles.purpleText}>
            <p>These prespecified exploratory endpoints were not adjusted for multiplicity; these descriptive results could represent chance findings.<sup>10</sup></p>
          </div>
          <ul className="footnotes">
            <li>
              <span className="hang">*</span>
              <span>Mean baseline values were 21.6 &plusmn; 24.4 for placebo and 13.7 &plusmn; 21.5 for CRENESSITY.<sup className="!font-normal">11</sup></span>
            </li>
        </ul>
        </div>
    </HcpAccordion>
    <HcpAccordion label="View bone age for growing patients" anchorId="growing-patients">

      <div className="container">
        <div className={styles.bone_age_section}>
          <h2 className={styles.bone_age_title}>
            Slowed bone age advancement and improved predicted <span className="text-nowrap mobile:text-wrap">height in children</span> taking CRENESSITY<sup>8,23,24</sup>
          </h2>

          <p className={styles.bone_age_subtitle}>Open-label extension: Bone age and predicted adult height</p>

          <div className={styles.bone_age_highlight}>
            <p>
              <strong>96%</strong> of growing children with advanced bone age at baseline treated with CRENESSITY experienced improved or stable bone age at 2 years<sup className="!font-normal">24*</sup>
            </p>
          </div>

          <p className={styles.bone_age_body}>
            Mean predicted adult height increased <strong>4.7 cm</strong> for the 38% of children with an improvement in bone age advancement of &ge;2 standard deviations.<sup>24&Dagger;</sup>
          </p>

          <div className={styles.bone_age_graph_row}>
            <div className={styles.bone_age_graph}>
              <p className={styles.bone_age_example}>
                Example male patient treated with CRENESSITY<sup className="!font-normal">23&Dagger;</sup>
              </p>
              <CustomImage
                src="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Bone-Age-Data.svg"
                width={836}
                height={402}
                alt="A chart showing bone age and predicted adult height for an example male patient treated with CRENESSITY at 2 years."
              />
            </div>

            <div className={styles.bone_age_side_note}>
              <p>
                Over the course of this patient&rsquo;s 2 years on CRENESSITY, his GC dose was reduced from 16 to <span className='text-nowrap'><strong>8.2 mg/m<sup className="!font-normal">2</sup>/day</strong></span>, and his androstenedione levels remained within the limits of normal.<sup>8</sup>
              </p>
            </div>
          </div>

          <p className={styles.bone_age_disclaimer}>
            These outcomes were not adjusted for multiplicity; these descriptive results could represent chance findings.
          </p>

          <ul className={`footnotes ${styles.bone_age_footnotes}`}>
            <li>
              <span className="hang">*</span>
              <span>Twenty-four children in the phase 3 trial had advanced bone age at baseline. Bone age assessments were done at baseline and at 2 years. No children were on an aromatase inhibitor at baseline. Stable or improved was defined as a bone age standard deviation score that did not worsen by more than 1 standard deviation.<sup className="!font-normal">24</sup></span>
            </li>
            <li>
              <sup className="hang">&dagger;</sup>
              <span>At 2 years, 9 of the 24 children (38%) had improvements in bone age-chronological age of &ge;2 standard deviations year and an increase in mean predicted adult height of 4.7 cm.<sup className="!font-normal">24</sup></span>
            </li>
            <li>
              <sup className="hang">&Dagger;</sup>
              <span>Patient initiated growth hormone at age 9.7 years.<sup className="!font-normal">23</sup></span>
            </li>
          </ul>
        </div>
      </div>

    </HcpAccordion>
  </section>
  );
}

